import { supabase } from "./supabase";
import type { ClaimsVariation } from "../types/database.types";

// ─── Types ────────────────────────────────────────────────────
export interface ClaimedSetVariation {
  variation_id: string;
  effective_amount: number;
}

interface ClaimedSetData {
  generated_at?: string;
  total_inc_gst?: number;
  retention_amount?: number;
  retention_pct?: number;
  progress?: { subcategory_id: string; claimed_percentage: number; claimed_amount: number }[];
  variations?: ClaimedSetVariation[];
}

// ─── Helpers ──────────────────────────────────────────────────
/**
 * Returns true if the claimed_set has NO usable (real, non-synthetic) variation
 * entries, i.e. the report is "legacy" and needs manual linking.
 */
export function hasNoUsableClaimedVariations(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  claimedSet: any | null | undefined
): boolean {
  if (!claimedSet) return true;
  const vars = claimedSet.variations;
  if (!Array.isArray(vars) || vars.length === 0) return true;
  // Every entry is synthetic (__invoiced_*) or has zero amount → treat as no real data
  return vars.every(
    (v: ClaimedSetVariation) =>
      String(v.variation_id ?? "").startsWith("__invoiced") ||
      Math.abs(Number(v.effective_amount ?? 0)) < 0.01
  );
}

/**
 * Compute the effective contribution of a variation after discount.
 */
export function variationEffectiveAmount(v: ClaimsVariation): number {
  const total = Number(v.total ?? 0);
  const discount = Number(v.discount_percent ?? 0);
  return discount > 0 ? total * (1 - discount / 100) : total;
}

// ─── Link Variations to Report ────────────────────────────────
/**
 * Writes (or overwrites) the `claimed_set.variations` array on a report,
 * preserving existing progress entries if present.
 */
export async function linkVariationsToReport(
  reportId: string,
  selectedVariations: ClaimsVariation[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Read current claimed_set (raw RPC to avoid generated-type issues)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: report, error: fetchErr } = await (supabase as any)
      .from("claims_reports")
      .select("claimed_set")
      .eq("id", reportId)
      .single();

    if (fetchErr) return { success: false, error: fetchErr.message };

    const existing: ClaimedSetData = (report?.claimed_set as ClaimedSetData) ?? {};

    // 2. Build new variation entries
    const newVariations: ClaimedSetVariation[] = selectedVariations.map((v) => ({
      variation_id: v.id,
      effective_amount: variationEffectiveAmount(v),
    }));

    // 3. Merge: preserve progress, update variations + timestamp
    const updated: ClaimedSetData = {
      ...existing,
      variations: newVariations,
      generated_at: existing.generated_at ?? new Date().toISOString(),
    };

    // 4. Write back
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: updateErr } = await (supabase as any)
      .from("claims_reports")
      .update({ claimed_set: updated })
      .eq("id", reportId);

    if (updateErr) return { success: false, error: updateErr.message };

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

// ─── Unlink Variations from Report ────────────────────────────
/**
 * Removes the `variations` key from `claimed_set`, reverting
 * the report to live date-based fallback.
 */
export async function unlinkVariationsFromReport(
  reportId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: report, error: fetchErr } = await (supabase as any)
      .from("claims_reports")
      .select("claimed_set")
      .eq("id", reportId)
      .single();

    if (fetchErr) return { success: false, error: fetchErr.message };

    const existing: ClaimedSetData = (report?.claimed_set as ClaimedSetData) ?? {};

    // Remove variations, keep everything else
    const { variations: _, ...rest } = existing;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: updateErr } = await (supabase as any)
      .from("claims_reports")
      .update({ claimed_set: Object.keys(rest).length > 0 ? rest : null })
      .eq("id", reportId);

    if (updateErr) return { success: false, error: updateErr.message };

    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
